import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Badge,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid2 as Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";

import { format, parse, sub } from "date-fns";
import { enAU } from "date-fns/locale";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import OrdersIcon from "../../icons/Orders";
import { makeStyles, withStyles } from "@mui/styles";
import PropTypes from "prop-types";
import { LargeActionButton } from "../Common/LargeActionButton";
import { DatePickerMobileActionBar } from "@/components/Common/DatePickerMobileActionBar";
import { arrivalCheckinMethod, arrivalStatus } from "../../util/receiveStatus";
import { getAmPmTimes, getCurrentTime } from "../../util/dateUtil";
import { useHistory } from "react-router-dom";

const StyledDialogTitle = withStyles({
	root: {
		padding: "8px 12px",
	},
})(DialogTitle);

const StyledTypography = withStyles({
	body2: {
		fontWeight: "400",
	},
	subtitle2: {
		fontWeight: "400",
	},
	caption: {
		fontWeight: "700",
		color: "gray",
	},
})(Typography) as typeof Typography;

const StyledDivider = withStyles({
	root: {
		margin: "6px 0 6px 0",
	},
})(Divider);

const StyledAccordionSummary = withStyles({
	root: {
		padding: "0",
		minHeight: "auto",
		"&$expanded": {
			minHeight: "auto",
		},
	},
	content: {
		margin: "0 0 -1px 0",
		"&$expanded": {
			margin: "0 0 -1px 0",
		},
	},
	expanded: {},
	expandIcon: {
		padding: "0px 12px 0px 12px",
		alignSelf: "start",
	},
})(AccordionSummary);

const StyledAccordionDetails = withStyles({
	root: {
		padding: "8px 0px 0px 0px",
		fontWeight: "400",
	},
})(AccordionDetails);

const StyledDialogActions = withStyles({
	root: {},
})(DialogActions);

const StyledFormControlLabel = withStyles({
	root: {
		paddingLeft: "5px",
	},
})(FormControlLabel);

const useStyles = makeStyles((theme) => ({
	body3: {
		fontSize: "0.875rem",
		fontWeight: "500",
	},
	subCaption: {
		fontSize: "0.750rem",
		fontWeight: "400",
		color: "brown",
	},
	statusTag: {
		color: "#FFFFFF",
		display: "inline",
		padding: "2px 6px",
	},
	complete: {
		backgroundColor: "green",
	},
	incomplete: {
		backgroundColor: "red",
	},
	datePickerCell: {
		paddingRight: "2px",
	},
	timePickerCell: {
		paddingleft: "2px",
	},
	datePicker: {
		"& .MuiInputBase-root": {
			height: "56px",
		},
		"& .MuiInputLabel-marginDense": {
			transform: "translate(14px, 20px) scale(1)",
		},
		"& .MuiInputLabel-shrink": {
			transform: "translate(14px, -6px) scale(0.75)",
		},
	},
	checkbox: {
		"& .MuiIconButton-label": {
			transform: "scale(1.85)",
		},
	},
}));
export default function ArrivalsCheckinBySsccScanDialog(props) {
	const { isOpen, onClose, onCompleteCheckin, currentScannedBarcode, isBlocking } = props;
	const history = useHistory();
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const WizardStep1 = useMemo(() => {
		return { step: 1, buttonText: "Next >" };
	}, []);
	const WizardStep2 = useMemo(() => {
		return { step: 2, buttonText: "Check in shipment" };
	}, []);
	const [wizardState, setWizardState] = useState(WizardStep1);
	const [arrivalsProgress, setArrivalsProgress] = useState([]);
	const minArrivalDate = sub(new Date(), { days: 14 });
	const defaultTime = getCurrentTime();
	const times = getAmPmTimes();
	const [form, setValue] = useState({
		arrivalDate: { value: new Date(), error: false, errorMessage: "" },
		arrivalTime: { value: defaultTime, error: false, errorMessage: "" },
	});
	const unblockRef = useRef(null);

	const isNullOrUndefinedOrEmpty = (value) => {
		return value === null || value === undefined || value === "";
	};
	const createFormValue = (value) => {
		const formValue = { error: false, errorMessage: "", value };
		if (isNullOrUndefinedOrEmpty(value)) {
			formValue.error = true;
			formValue.errorMessage = "Required";
		}
		return formValue;
	};
	const clearForm = useCallback(() => {
		setValue({
			arrivalDate: { value: new Date(), error: false, errorMessage: "" },
			arrivalTime: { value: defaultTime, error: false, errorMessage: "" },
		});
		setArrivalsProgress([]);
		setWizardState(WizardStep1);
	}, [WizardStep1, defaultTime]);
	const checkinShipment = () => {
		arrivalsProgress.forEach((p) => {
			const arrivalDateTimeString = `${format(form.arrivalDate.value, "dd-MM-yyyy")} ${form.arrivalTime.value}`;
			p.arrival.dateArrived = parse(arrivalDateTimeString, "dd-MM-yyyy hh:mm a", form.arrivalDate.value);
		});
		onCompleteCheckin(arrivalsProgress);
	};
	const handleArrivalDateChange = (date) => {
		const formValue = createFormValue(date);
		setValue({ ...form, arrivalDate: formValue });
	};
	const handleArrivalTimeChange = (event) => {
		const formValue = createFormValue(event.target.value);
		setValue({ ...form, arrivalTime: formValue });
	};
	const handleClickClose = () => {
		onClose(true);
	};
	const handleNext = () => {
		if (wizardState.step === 1) {
			setWizardState(WizardStep2);
		}
	};
	const handleCheckinShipment = () => {
		checkinShipment();
	};
	const handleArrivalPackageCheckedChange = (arrival, ssccPackage, checked) => {
		ssccPackage.timestamp = checked ? new Date() : null;
		ssccPackage.checkinMethod = checked ? arrivalCheckinMethod.manual : null;
		ssccPackage.checked = checked;
		arrival.numberReceived = arrival.packages.filter((p) => p.checked).length;
		if (arrival.numberReceived === arrival.packages.length) {
			arrival.inFull = arrivalStatus.complete;
		} else {
			arrival.inFull = arrivalStatus.incomplete;
		}
		setArrivalsProgress([...arrivalsProgress]);
	};
	const CompleteTag = (tagProps) => {
		const { arrivalProgress } = tagProps;
		const className =
			arrivalProgress.arrival.inFull === arrivalStatus.complete
				? `${classes.statusTag} ${classes.complete}`
				: `${classes.statusTag} ${classes.incomplete}`;

		return <div className={className}>{arrivalProgress.arrival.inFull}</div>;
	};
	useEffect(() => {
		const currentAsn = currentScannedBarcode?.relatedAsn;
		const arrivalProgress = arrivalsProgress.find((p) => currentAsn && p.asn.asnId === currentAsn.asnId);
		const existedAsn = arrivalProgress?.asn;
		//New ASN => New Arrival Recrod
		if (currentAsn && !existedAsn) {
			const arrivalData = {
				dateArrived: new Date(),
				consignmentNumber: currentAsn.consignmentNumber,
				numberReceived: 1,
				carrier: currentAsn.carrier,
				inFull: currentAsn.totalNumberOfPackages === 1 ? arrivalStatus.complete : arrivalStatus.incomplete,
				createdByUserName: null, // To be set on parent page
				createdByUserId: null, // To be set on parent page
				checkinMethod: arrivalCheckinMethod.ssccScan,
				asnId: currentAsn.asnId,
				packages: currentAsn.analysed.toBeReceivedPackages.map((pak) => {
					const scanned = currentScannedBarcode.value === pak.sscc;
					return {
						...pak,
						timestamp: scanned ? new Date() : null,
						checkinMethod: scanned ? arrivalCheckinMethod.ssccScan : null,
						checked: scanned,
					};
				}),
			};
			arrivalsProgress.push({
				arrival: arrivalData,
				asn: currentAsn,
				scannedSsccList: [currentScannedBarcode.value], // Technically this should be subset of asn.ssccList
			});
			setArrivalsProgress([...arrivalsProgress]);
			// With any successful scan that will update the UI. Return to first step
			setWizardState(WizardStep1);
			return;
		} else if (existedAsn) {
			// ASN exists => Arrival record created in arrivalsProgress
			const arrivalPackage = arrivalProgress.arrival.packages.find((p) => p.sscc === currentScannedBarcode.value);
			arrivalPackage.checkinMethod = arrivalCheckinMethod.ssccScan;
			arrivalPackage.checked = true;
			arrivalPackage.timestamp = new Date();
			arrivalProgress.arrival.numberReceived = arrivalProgress.arrival.packages.filter((p) => p.checked).length;
			arrivalProgress.scannedSsccList.push(currentScannedBarcode.value);

			if (arrivalProgress.arrival.numberReceived === arrivalProgress.arrival.packages.length) {
				arrivalProgress.arrival.inFull = arrivalStatus.complete;
			}
			setArrivalsProgress([...arrivalsProgress]);
			// With any successful scan that will update the UI. Return to first step
			setWizardState(WizardStep1);
		}
		// to prevent react from going into infinite update after removing condition to only update new scanned barcode. Old barcodes can be rescand
		currentScannedBarcode.relatedAsn = null;
	}, [currentScannedBarcode, arrivalsProgress, WizardStep1]);

	useEffect(() => {
		// Reset dialog state on each close
		if (!isOpen) clearForm();
	}, [clearForm, isOpen]);

	useEffect(() => {
		const blockNavigation = () => {
			if (unblockRef.current) {
				unblockRef.current(); // Unblock any existing block before setting a new one
			}
			const unblockNavigation = history.block((location, action) => {
				if (action === "POP") {
					if (onClose(true)) {
						unblockNavigation();
					} else {
						blockNavigation();
					}
					return false;
				}
			}) as () => void;
			unblockRef.current = unblockNavigation;
		};

		blockNavigation();

		return () => {
			if (unblockRef.current) {
				unblockRef.current(); // Clean up the block
			}
		};
	}, [history, onClose, isBlocking]);

	return (
		<Dialog fullScreen open={isOpen} onClose={handleClickClose} scroll="body">
			<StyledDialogTitle>
				<Grid container spacing={0} direction="column">
					<Grid container justifyContent="flex-end" size="grow">
						<IconButton onClick={handleClickClose} size="small" edge="end">
							<CloseIcon color="primary" />
						</IconButton>
					</Grid>
					<Grid container spacing={0} direction="column" alignItems="center" size="grow">
						<Grid size={12}>
							<Badge overlap="rectangular" badgeContent={arrivalsProgress.length} color="error">
								<OrdersIcon />
							</Badge>
						</Grid>
						<Grid size={12}>
							<StyledTypography variant="subtitle1" component="div">
								<strong>Check in invoice(s)</strong>
							</StyledTypography>
						</Grid>
						<Grid size={12}>
							<StyledTypography variant="subtitle2" component="div">
								To check in the invoice(s), scan all carton
								<br />
								SSCC and then click next.
							</StyledTypography>
						</Grid>
					</Grid>
					<Grid size={12}>
						<StyledDivider />
					</Grid>
				</Grid>
			</StyledDialogTitle>
			<DialogContent>
				{wizardState.step === 1 && (
					<Grid container direction="column" spacing={2} size="grow">
						{arrivalsProgress.map((p, index) => (
							<Grid key={index} size={12}>
								<Accordion elevation={0}>
									<StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Grid container direction="column" spacing={0} size="grow">
											<Grid container direction="row" spacing={0} style={{ paddingBottom: "10px" }} size="grow">
												<Grid size={7}>
													<StyledTypography variant="caption" component="div">
														Invoice Number
													</StyledTypography>
													<StyledTypography variant="body2" component="div">
														<strong>{p.asn.invoiceNumber}</strong>
													</StyledTypography>
													<StyledTypography variant="subtitle2" component="div">
														<CompleteTag arrivalProgress={p} />
													</StyledTypography>
												</Grid>
												<Grid size={5}>
													<Grid
														container
														direction="column"
														alignItems="flex-end"
														justifyContent="flex-end"
														style={{ height: "100%" }}
													>
														<StyledTypography variant="caption" component="div">
															Checking In
														</StyledTypography>
														{p.asn.analysed.hasReceive > 0 && (
															/* Summary row about previously received + currently receving. Should add up to the ASN totalNumberOfPackages*/
															<StyledTypography variant="caption" component="div">
																<StyledTypography className={classes.subCaption} component="div">
																	Total:&nbsp;
																	{p.asn.analysed.previouslyReceivedPackagesTotal + p.arrival.numberReceived}
																	&nbsp;of {p.asn.totalNumberOfPackages}
																</StyledTypography>
															</StyledTypography>
														)}
														<StyledTypography variant="subtitle2" component="div">
															{p.asn.analysed.hasReceive && "Now: "}
															<StyledTypography className={classes.body3} component="span">
																{p.arrival.numberReceived}&nbsp;
															</StyledTypography>
															of {p.asn.analysed.toBeReceivedPackagesTotal}
														</StyledTypography>
													</Grid>
												</Grid>
											</Grid>

											<Grid container direction="row" spacing={0} size="grow">
												<Grid size={7}>
													<StyledTypography variant="caption" component="div">
														Purchase Order
													</StyledTypography>
													<StyledTypography variant="body2" className={classes.body3} component="div">
														{p.asn.purchaseOrderId}
													</StyledTypography>
												</Grid>
												<Grid size={5}>
													<Grid
														container
														direction="column"
														alignItems="flex-end"
														justifyContent="flex-end"
														style={{ height: "100%" }}
													>
														<StyledTypography variant="caption" component="div">
															Carrier
														</StyledTypography>
														<StyledTypography variant="body2" className={classes.body3} component="div">
															{p.asn.carrier}
														</StyledTypography>
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									</StyledAccordionSummary>
									<StyledAccordionDetails>
										<Grid container direction="column" spacing={0} size="grow">
											<Grid container style={{ paddingBottom: "5px" }} size="grow">
												<Grid size={12}>
													<StyledTypography variant="caption" component="div">
														Consignment number
													</StyledTypography>
													<StyledTypography variant="body2" className={classes.body3} component="div">
														{p.asn.consignmentNumber}
													</StyledTypography>
												</Grid>
											</Grid>
											<Grid container size="grow">
												<Grid size={12}>
													<StyledTypography variant="caption" component="div">
														SSCC Barcodes
													</StyledTypography>
												</Grid>
												{p.arrival.packages.map((pak) => (
													<Grid key={pak.sscc} size={12}>
														<StyledFormControlLabel
															control={
																<Checkbox
																	color="primary"
																	checked={pak.checked}
																	name={pak.sscc}
																	className={classes.checkbox}
																	onChange={(e) => handleArrivalPackageCheckedChange(p.arrival, pak, e.target.checked)}
																/>
															}
															label={pak.sscc}
														/>
													</Grid>
												))}
											</Grid>
										</Grid>
									</StyledAccordionDetails>
								</Accordion>
								<StyledDivider />
							</Grid>
						))}
					</Grid>
				)}
				{wizardState.step === 2 && (
					<Grid container direction="row" size={12}>
						<Grid size={6} className={classes.datePickerCell}>
							<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
								<DatePicker
									className={classes.datePicker}
									label="Arrival Date"
									format="dd/MM/yyyy"
									value={form.arrivalDate.value}
									disableFuture
									minDate={minArrivalDate}
									closeOnSelect
									slots={{ actionBar: DatePickerMobileActionBar }}
									slotProps={{
										textField: {
											id: "arrival-date",
											variant: "outlined",
											fullWidth: true,
											size: "small",
											error: form.arrivalDate.error,
											helperText: form.arrivalDate.errorMessage,
											required: true,
										},
										actionBar: {
											actions: isMobile ? ["cancel", "accept"] : [],
											onSetDefaultDate: () => {
												if (!form.arrivalDate.value) handleArrivalDateChange(new Date());
											},
										} as any,
									}}
									onChange={handleArrivalDateChange}
								/>
							</LocalizationProvider>
						</Grid>
						<Grid size={6} className={classes.timePickerCell}>
							<FormControl variant="outlined" fullWidth error={form.arrivalTime.error}>
								<InputLabel id="arrival-time-input-label">Time *</InputLabel>
								<Select
									value={form.arrivalTime.value}
									label="Time"
									labelId="arrival-time-input-label"
									id="arrival-time-input"
									onChange={handleArrivalTimeChange}
									required
								>
									{times.map((time) => (
										<MenuItem key={time} value={time}>
											{time}
										</MenuItem>
									))}
								</Select>
								<FormHelperText>{form.arrivalTime.errorMessage}</FormHelperText>
							</FormControl>
						</Grid>
					</Grid>
				)}
			</DialogContent>
			<StyledDialogActions>
				<Grid container justifyContent="flex-start" size="grow" sx={{ width: "100%" }}>
					{wizardState.step === 1 && (
						<LargeActionButton onClick={handleNext} disabled={arrivalsProgress.length === 0}>
							{wizardState.buttonText}
						</LargeActionButton>
					)}
					{wizardState.step === 2 && (
						<LargeActionButton onClick={handleCheckinShipment}>{wizardState.buttonText}</LargeActionButton>
					)}
				</Grid>
			</StyledDialogActions>
		</Dialog>
	);
}

ArrivalsCheckinBySsccScanDialog.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	currentScannedBarcode: PropTypes.object,
	onClose: PropTypes.func.isRequired,
	onCompleteCheckin: PropTypes.func.isRequired,
	isBlocking: PropTypes.bool.isRequired,
};
